// Page translation for the screens that still have French written in their
// templates. When the language is not French, every text node and
// placeholder/title/aria-label rendered in the page (toasts and SweetAlert
// dialogs included) is looked up in ./dom/<lang>.json:
//   texts:    exact French string → translation
//   patterns: French text with {0}, {1}… slots (dynamic values) → translation
// A MutationObserver keeps re-rendered content translated. The French source
// of each node is remembered, so switching back to French restores it.
// New code should use $t() with src/i18n/locales instead of relying on this.
import { watch } from "vue";
import i18n, { currentLocale, type Locale } from "./index";

interface DomDict {
  texts: Record<string, string>;
  patterns: string[][]; // [French with {0} slots, translation]
}

interface Compiled {
  exact: Map<string, string>;
  lower: Map<string, string>;
  patterns: { re: RegExp; out: string }[];
  cache: Map<string, string | null>;
}

const LOADERS: Partial<Record<Locale, () => Promise<{ default: DomDict }>>> = {
  en: () => import(/* webpackChunkName: "i18n-dom-en" */ "./dom/en.json"),
  ar: () => import(/* webpackChunkName: "i18n-dom-ar" */ "./dom/ar.json"),
};

const ATTRS = ["placeholder", "title", "aria-label"];
const BUTTON_INPUTS = new Set(["button", "submit", "reset"]);
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "TEXTAREA", "CODE", "PRE", "NOSCRIPT", "INPUT", "SELECT"]);
// Trailing marks kept as-is around a known label ("Ville :", "Nom *", "Chercher...")
const SUFFIX_RE = /^(.*?)(\s*(?:\(\*\)|\*|:|\.{3}|…)+)$/s;
const LETTER_RE = /[A-Za-zÀ-ÿ]/;

const compiled: Partial<Record<Locale, Compiled>> = {};
let active: Compiled | null = null;

// French source + translation we wrote (and with which dictionary), per text
// node and per attribute
interface Written {
  src: string;
  out: string;
  dict: Compiled;
}
const textState = new WeakMap<Text, Written>();
const attrState = new WeakMap<Element, Map<string, Written>>();

function norm(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function escapeRe(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function compile(dict: DomDict): Compiled {
  const exact = new Map<string, string>();
  const lower = new Map<string, string>();
  for (const [fr, tr] of Object.entries(dict.texts)) {
    exact.set(fr, tr);
    if (!lower.has(fr.toLowerCase())) lower.set(fr.toLowerCase(), tr);
  }
  const patterns = dict.patterns
    .map(([fr, out]) => {
      const parts = fr.split(/\{\d+\}/);
      const slots = fr.match(/\{\d+\}/g) || [];
      let src = "^";
      parts.forEach((part, i) => {
        src += escapeRe(part).replace(/ /g, "\\s*");
        if (i < slots.length) src += "(.+?)";
      });
      // most specific (longest fixed text) first
      return { re: new RegExp(src + "$", "s"), out, weight: parts.join("").length };
    })
    .sort((a, b) => b.weight - a.weight)
    .map(({ re, out }) => ({ re, out }));
  return { exact, lower, patterns, cache: new Map() };
}

function lookup(dict: Compiled, text: string): string | undefined {
  const hit = dict.exact.get(text);
  if (hit !== undefined) return hit;
  const low = dict.lower.get(text.toLowerCase());
  if (low !== undefined) {
    // "STATUT" (written in capitals) → "STATUS"
    return text === text.toUpperCase() ? low.toUpperCase() : low;
  }
  return undefined;
}

function translateText(dict: Compiled, raw: string): string | null {
  const text = norm(raw);
  if (!text || !LETTER_RE.test(text)) return null;
  if (dict.cache.has(text)) return dict.cache.get(text) ?? null;

  let out: string | null = lookup(dict, text) ?? null;
  if (out === null) {
    const m = SUFFIX_RE.exec(text);
    if (m && m[1]) {
      const base = lookup(dict, m[1].trim());
      if (base !== undefined) out = base + m[2];
    }
  }
  if (out === null) {
    for (const p of dict.patterns) {
      const m = p.re.exec(text);
      if (!m) continue;
      // slot values (statuses, names, numbers…) are translated when known
      out = p.out.replace(/\{(\d+)\}/g, (_, n) => {
        const value = (m[Number(n) + 1] ?? "").trim();
        return lookup(dict, value) ?? value;
      });
      break;
    }
  }
  if (dict.cache.size > 5000) dict.cache.clear();
  dict.cache.set(text, out);
  if (out === null) return null;
  // keep the spacing around the text (inline elements next to it)
  const lead = raw.match(/^\s*/)?.[0] ?? "";
  const trail = raw.match(/\s*$/)?.[0] ?? "";
  return lead + out + trail;
}

function skipElement(el: Element): boolean {
  if (SKIP_TAGS.has(el.tagName)) return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return el.hasAttribute("data-no-translate") || el.classList.contains("ql-editor");
}

function isInSkipped(node: Node): boolean {
  for (let el = node.parentElement; el; el = el.parentElement) {
    if (skipElement(el)) return true;
  }
  return false;
}

function processText(node: Text) {
  const current = node.nodeValue ?? "";
  const state = textState.get(node);
  // our translation is in place: keep it, unless the language changed
  if (state && current === state.out && state.dict === active) return;
  // source = the French we replaced, or the new value Vue just wrote
  const src = state && current === state.out ? state.src : current;
  textState.delete(node);
  const out = active ? translateText(active, src) : null;
  if (active && out !== null && out !== src) {
    textState.set(node, { src, out, dict: active });
    if (current !== out) node.nodeValue = out;
  } else if (src !== current) {
    node.nodeValue = src;
  }
}

function processAttr(el: Element, name: string) {
  const current = el.getAttribute(name);
  let states = attrState.get(el);
  const state = states?.get(name);
  if (current === null) {
    states?.delete(name);
    return;
  }
  if (state && current === state.out && state.dict === active) return;
  const src = state && current === state.out ? state.src : current;
  states?.delete(name);
  const out = active ? translateText(active, src) : null;
  if (active && out !== null && out !== src) {
    if (!states) attrState.set(el, (states = new Map()));
    states.set(name, { src, out, dict: active });
    if (current !== out) el.setAttribute(name, out);
  } else if (src !== current) {
    el.setAttribute(name, src);
  }
}

function processElementAttrs(el: Element) {
  for (const name of ATTRS) if (el.hasAttribute(name)) processAttr(el, name);
  if (el.tagName === "INPUT" && BUTTON_INPUTS.has((el as HTMLInputElement).type) && el.hasAttribute("value")) {
    processAttr(el, "value");
  }
}

function walk(root: Node) {
  if (root.nodeType === Node.TEXT_NODE) {
    if (!isInSkipped(root)) processText(root as Text);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  const rootEl = root as Element;
  if (isInSkipped(rootEl)) return;
  // inputs are skipped for their content, but their placeholder is translated
  processElementAttrs(rootEl);
  if (skipElement(rootEl)) return;

  const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        processElementAttrs(el);
        return skipElement(el) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_SKIP;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let node: Node | null;
  while ((node = walker.nextNode())) processText(node as Text);
}

const observer = new MutationObserver((mutations) => {
  for (const m of mutations) {
    if (m.type === "characterData") {
      if (!isInSkipped(m.target)) processText(m.target as Text);
    } else if (m.type === "attributes") {
      const el = m.target as Element;
      if (m.attributeName && !isInSkipped(el)) processAttr(el, m.attributeName);
    } else {
      m.addedNodes.forEach((n) => walk(n));
    }
  }
});

let observing = false;
let applyId = 0;

async function apply(locale: Locale) {
  const id = ++applyId;
  const load = LOADERS[locale];
  if (!load) {
    // French: put every source text back
    active = null;
    walk(document.body);
    return;
  }
  if (!compiled[locale]) {
    try {
      compiled[locale] = compile((await load()).default);
    } catch {
      return; // chunk failed to load: the page stays in French
    }
  }
  if (id !== applyId) return; // the language changed again meanwhile
  active = compiled[locale] ?? null;
  walk(document.body);
  if (!observing) {
    observing = true;
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...ATTRS, "value"],
    });
  }
}

export function startDomTranslation() {
  apply(currentLocale());
  watch(
    () => i18n.global.locale.value as Locale,
    (locale) => apply(locale)
  );
}
